import React, { useEffect } from 'react';
import {
  Message,
  MessageType,
  BotAgentMessage,
  CustomMessage,
  Conversation,
} from '@tomis-tech/types';

import { useSession } from '../context';
import MessageContent from './MessageContent';
import { logError } from '.././sentry-iframe';
import QuickReplies from './QuickReplies';
import ChatCustomMessage from './ChatCustomMessage';
import { processDialogflowMessage } from '../utils';
import { SubtleButton } from './SmsForm';
import { markMessageAsRead } from '../services/markMessageAsRead';
import { trackGA4Event } from '../../tracking';
import { GA4Event } from '../../tracking/event-types';
import { getFirebaseUserId } from '../../firebase';

type ChatMessageProps = {
  message: Message;
};

const GRAY = '#929292';
const GRAY_LIGHT = '#E1E1E1';
const GRAY_DARK = '#585858';
const BLUE = 'rgb(0,112,255)';

/** Automatically marking all messages as seen when they are rendered,
 * whether or not they actually appear on screen */
const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { conversation, site } = useSession();

  const firebaseUserId = getFirebaseUserId();

  /** Has the message been seen by the customer? */
  const messageHasBeenSeen = Boolean(message.seenBy?.includes(firebaseUserId));

  // Mark Message as Seen
  useEffect(() => {
    if (!messageHasBeenSeen && firebaseUserId) {
      markMessageAsRead({
        conversationId: conversation.ref.id,
        messageId: message.id,
        userId: firebaseUserId,
      });
    }
  }, [messageHasBeenSeen, conversation.ref.id, message.id, firebaseUserId]);

  const messageType =
    message.type === MessageType.User ? message.userType : message.type;
  const isCustomer = messageType === 'customer';
  const isBot = messageType === MessageType.BotAgent;
  const isSupport = messageType === 'support';
  const isSystem = messageType === MessageType.System;
  const isCustom = messageType === MessageType.Custom;

  const { quickReplies, textMessages } = processDialogflowMessage(message);

  /** Bot is configured for SMS Chat */
  const isSmsEnabled = site.features.includes('liveChatSMS');
  /** The Bot response includes Dialogflow action to show lead form */
  const isShowLeadFormMessage =
    isBot &&
    (message as BotAgentMessage).dialogflowQueryResult?.action ===
      'show_lead_form';
  /** SMS Chat is enabled + this Dialogflow action is to show lead form */
  const showSmsChatToggle = Boolean(isSmsEnabled && isShowLeadFormMessage);

  /** If SMS Chat is enabled, show lead form by updating `conversationMode` */
  async function showLeadForm() {
    trackGA4Event(GA4Event.ClickedRequestSms);

    await conversation.ref
      .update({ conversationMode: 'liveChatSMS' } as Partial<Conversation>)
      .catch(logError);
  }

  return (
    <>
      {quickReplies && (
        <QuickReplies
          data={quickReplies}
          message={message as BotAgentMessage}
        />
      )}
      <div>
        {isCustom ? (
          <ChatCustomMessage message={message as CustomMessage} />
        ) : (
          textMessages.map((text, index) => {
            // no message bubble if blank response from Dialogflow
            if (text === '' || text === ' ') {
              return <React.Fragment key={index} />;
            }

            return (
              <div key={index}>
                <li
                  key={index}
                  css={{
                    display: 'flex',
                    justifyContent: isCustomer
                      ? 'flex-end'
                      : isSupport || isBot
                      ? 'flex-start'
                      : 'center',
                    wordBreak: 'break-word',
                  }}
                >
                  <div>
                    <span
                      css={{
                        display: 'block',
                        borderRadius: '12px',
                        width: 'fit-content',
                        minWidth: 33,
                        maxWidth: 280,
                        minHeight: messageType === 'system' ? 12 : 39,
                        margin: '6px 0',
                        background: isCustomer
                          ? BLUE
                          : isSystem
                          ? 'none'
                          : GRAY_LIGHT,
                        color: isCustomer
                          ? 'white'
                          : isSystem
                          ? GRAY
                          : GRAY_DARK,
                        fontSize: messageType === 'system' ? 12 : 16,
                        textTransform:
                          messageType === 'system' ? 'uppercase' : 'none',
                        letterSpacing: 0,
                        padding: messageType === 'system' ? '8px' : '10px 12px',
                      }}
                    >
                      <MessageContent>{text}</MessageContent>
                    </span>
                    {isSupport && (
                      <div
                        css={{
                          fontSize: 12,
                          color: GRAY,
                          padding: '0 12px 6px 12px',
                        }}
                      >
                        Support Agent
                      </div>
                    )}
                  </div>
                </li>
                {showSmsChatToggle && (
                  <SubtleButton onClick={showLeadForm}>
                    Request text message
                  </SubtleButton>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default ChatMessage;
